import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Home, FolderTree, Sofa, MessageSquare, Trash2, LogOut, Sun, Moon, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { AdminProvider, useAdmin } from "@/context/AdminContext";
import { useLang, useT, usePageTitle, LANGS } from "@/i18n/LanguageContext";

// Separate inner component so it can consume the AdminContext
function AdminLayoutContent() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { unread, refreshUnread, unsavedRef } = useAdmin();
    const [light, setLight] = useState(() => localStorage.getItem("admin-theme") === "light");
    const { lang, setLang } = useLang();
    const t = useT();
    // Destination the user tried to reach while a page had unsaved changes.
    const [pendingNav, setPendingNav] = useState(null);
    const [leaving, setLeaving] = useState(false);

    const NAV = [
        { to: "/admin", label: t("admin.nav.overview"), icon: LayoutDashboard, end: true, id: "overview" },
        { to: "/admin/homepage", label: t("admin.nav.homepage"), icon: Home, id: "homepage" },
        { to: "/admin/categories", label: t("admin.nav.categories"), icon: FolderTree, id: "categories" },
        { to: "/admin/products", label: t("admin.nav.products"), icon: Sofa, id: "products" },
        { to: "/admin/inquiries", label: t("admin.nav.inquiries"), icon: MessageSquare, id: "inquiries" },
        { to: "/admin/trash", label: t("admin.nav.trash"), icon: Trash2, id: "trash" },
    ];

    // Tab title follows the active admin section, e.g. "Produse · Administrare — Melilux".
    const { pathname } = useLocation();
    const activeNav = [...NAV].reverse().find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
    usePageTitle(`${activeNav ? `${activeNav.label} · ` : ""}${t("meta.title.admin")}`);

    const toggle = () => setLight((v) => {
        const next = !v;
        localStorage.setItem("admin-theme", next ? "light" : "dark");
        return next;
    });

    useEffect(() => {
        refreshUnread();
    }, []);

    const guardedNavClick = (e, to) => {
        if (unsavedRef.current) {
            e.preventDefault();
            setPendingNav(to);
        }
    };

    const leaveAndDiscard = () => {
        const to = pendingNav;
        unsavedRef.current = null;
        setPendingNav(null);
        if (to) navigate(to);
    };

    const leaveAfterSave = async () => {
        const guard = unsavedRef.current;
        const to = pendingNav;
        setLeaving(true);
        try {
            if (guard?.save) await guard.save();
            unsavedRef.current = null;
            setPendingNav(null);
            if (to) navigate(to);
        } finally {
            setLeaving(false);
        }
    };

    const handleLogout = async () => {
        if (unsavedRef.current && !window.confirm(t("admin.home.leave.body"))) return;
        unsavedRef.current = null;
        await logout();
        toast.success("Signed out");
        navigate("/admin/login");
    };

    return (
        <div className={`${light ? "theme-white" : ""} min-h-screen flex bg-melilux-bg text-melilux-cream font-sans`}>
            {/* Sidebar */}
            <aside
                className="w-60 shrink-0 border-r border-white/10 bg-melilux-bg flex flex-col h-screen sticky top-0"
                data-testid="admin-sidebar"
            >
                <div className="px-6 py-6 border-b border-white/10">
                    <p className="font-serif text-2xl text-melilux-cream">
                        Melilux<span className="text-melilux-gold">.</span>
                    </p>
                    <p className="text-[0.65rem] uppercase tracking-[0.3em] text-melilux-cream/40 mt-1">
                        {t("admin.nav.console")}
                    </p>
                </div>

                <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
                    {NAV.map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                onClick={(e) => guardedNavClick(e, n.to)}
                                data-testid={`admin-nav-${n.id}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 text-[0.78rem] uppercase tracking-[0.18em] transition-colors ${
                                        isActive
                                            ? "bg-white/5 text-melilux-gold border-l-2 border-melilux-gold"
                                            : "text-melilux-cream/70 hover:text-melilux-cream hover:bg-white/5 border-l-2 border-transparent"
                                    }`
                                }
                            >
                                <Icon size={15} strokeWidth={1.5} />
                                <span className="flex-1">{n.label}</span>
                                {n.id === "inquiries" && unread > 0 && (
                                    <span className="text-[0.6rem] bg-melilux-gold text-melilux-bg px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                                        {unread}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="border-t border-white/10 p-4">
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="admin-view-site"
                        className="w-full flex items-center justify-between text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/50 hover:text-melilux-gold transition-colors mb-4"
                    >
                        <span>{t("admin.nav.viewSite")}</span>
                        <ExternalLink size={13} strokeWidth={1.5} />
                    </a>
                    <button
                        onClick={toggle}
                        data-testid="admin-theme-toggle"
                        className="w-full flex items-center justify-between text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/50 hover:text-melilux-gold transition-colors mb-4"
                    >
                        <span>{light ? t("admin.nav.lightMode") : t("admin.nav.darkMode")}</span>
                        {light ? <Sun size={13} strokeWidth={1.5} /> : <Moon size={13} strokeWidth={1.5} />}
                    </button>

                    {/* Language switcher */}
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/50">
                            {t("admin.nav.language")}
                        </span>
                                            <div className="flex items-center gap-0.5">
                                                {LANGS.map(({ code, label }, i) => (
                                                    <span key={code} className="flex items-center">
                                    <button
                                        onClick={() => setLang(code)}
                                        className={`text-[0.62rem] tracking-[0.2em] uppercase px-1 transition-colors ${
                                            lang === code
                                                ? "text-melilux-gold"
                                                : "text-melilux-cream/40 hover:text-melilux-cream/70"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                                        {i < LANGS.length - 1 && (
                                                            <span className="text-melilux-cream/20 text-[0.5rem] select-none">/</span>
                                                        )}
                                </span>
                                                ))}
                                            </div>
                                        </div>

                    <div className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/40">
                        {t("admin.nav.signedIn")}
                    </div>
                    <div className="text-sm font-medium text-melilux-cream mt-1 truncate" data-testid="admin-user-email">
                        {user?.email}
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout"
                        className="mt-4 w-full flex items-center justify-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] border border-white/15 hover:border-melilux-gold hover:text-melilux-gold py-2.5 transition-colors"
                    >
                        <LogOut size={13} strokeWidth={1.5} />
                        {t("admin.nav.signOut")}
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 bg-melilux-surface min-h-screen">
                <Outlet />
            </div>

            {/* Unsaved-changes dialog */}
            {pendingNav && (
                <div
                    className="fixed inset-0 z-[70] bg-melilux-bg/85 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setPendingNav(null)}
                    data-testid="unsaved-dialog"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-melilux-bg border border-white/15 p-8"
                    >
                        <p className="overline">{t("admin.home.unsaved")}</p>
                        <p className="text-sm text-melilux-cream/75 mt-4 leading-relaxed">
                            {t("admin.home.leave.body")}
                        </p>
                        <div className="mt-8 flex flex-col gap-3">
                            <button
                                onClick={leaveAfterSave}
                                disabled={leaving}
                                data-testid="unsaved-save-leave"
                                className="w-full py-3 text-[0.7rem] uppercase tracking-[0.28em] bg-melilux-gold text-melilux-bg hover:bg-melilux-goldBright transition-colors disabled:opacity-60"
                            >
                                {leaving ? t("admin.home.saving") : t("admin.home.leave.saveGo")}
                            </button>
                            <button
                                onClick={leaveAndDiscard}
                                disabled={leaving}
                                data-testid="unsaved-discard"
                                className="w-full py-3 text-[0.7rem] uppercase tracking-[0.28em] border border-white/15 text-melilux-cream/75 hover:border-destructive hover:text-destructive transition-colors"
                            >
                                {t("admin.home.leave.discard")}
                            </button>
                            <button
                                onClick={() => setPendingNav(null)}
                                disabled={leaving}
                                data-testid="unsaved-stay"
                                className="w-full py-3 text-[0.7rem] uppercase tracking-[0.28em] border border-white/15 hover:border-melilux-cream/40 transition-colors"
                            >
                                {t("admin.home.leave.stay")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminLayout() {
    return (
        <AdminProvider>
            <AdminLayoutContent />
        </AdminProvider>
    );
}
